import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import { IoCloseCircleOutline } from "react-icons/io5";
import { motion } from "framer-motion";
import "./custom-tooltip.scss";

type CustomTooltipProps = {
    id: string;
    placement?: "top" | "bottom" | "left" | "right";
    customContent?: ReactNode;
    contentString?: string;
    open?: boolean;
    onClose?: () => void;
    animate?: boolean;
    autoCloseTime?: number | null;
    openDelay?: number;
    className?: string;
    style?: CSSProperties;
    /**
     * Puzzle tile onboarding only: portaled fixed tooltip above header/overflow clipping.
     */
    adjustForVirtualKeyboard?: boolean;
};

function DefaultCustomTooltip({
    id,
    placement = "top",
    customContent = null,
    contentString = "",
    open = false,
    onClose = () => {},
    animate = false,
    autoCloseTime = null,
    openDelay = 0,
    className = "!tw-text-[#6C26C4]",
    style = {},
}: CustomTooltipProps) {
    const [show, setShow] = useState(false);

    const bounceTransition = {
        type: "spring" as const,
        stiffness: 400,
        damping: 10,
    };

    const bounceVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: { scale: 1, opacity: 1 },
        exit: { scale: 0.8, opacity: 0 },
    };

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        if (open && autoCloseTime) {
            timer = setTimeout(onClose, autoCloseTime);
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [open, autoCloseTime, onClose]);

    useEffect(() => {
        if (open) {
            setTimeout(() => setShow(true), openDelay);
        } else {
            setShow(false);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Tooltip
            id={id}
            className={`custom-tooltip${className ? " " + className : ""}`}
            place={placement}
            clickable
            style={style}
            render={() => (
                <div className="content">
                    <motion.div
                        initial="hidden"
                        animate={animate ? "visible" : ""}
                        exit="exit"
                        variants={bounceVariants}
                        transition={bounceTransition}
                    >
                        <button onClick={onClose} className="close-btn tw-cursor-pointer">
                            <IoCloseCircleOutline fontSize={17} />
                        </button>

                        <div className="content-texts">
                            {customContent ? customContent : <p className="mb-0">{contentString}</p>}
                        </div>
                    </motion.div>
                </div>
            )}
            isOpen={show}
        />
    );
}

function PuzzleTileCustomTooltip({
    id,
    placement = "top",
    customContent = null,
    contentString = "",
    open = false,
    onClose = () => {},
    animate = false,
    autoCloseTime = null,
    openDelay = 0,
    className = "!tw-text-[#6C26C4]",
    style = {},
}: CustomTooltipProps) {
    const anchorSelect = useMemo(() => `[data-tooltip-id='${id.replace(/'/g, "\\'")}']`, [id]);
    const mergedStyle = useMemo(
        (): CSSProperties => ({
            zIndex: 200,
            ...style,
        }),
        [style],
    );

    const bounceTransition = {
        type: "spring" as const,
        stiffness: 400,
        damping: 10,
    };

    const bounceVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: { scale: 1, opacity: 1 },
        exit: { scale: 0.8, opacity: 0 },
    };

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        if (open && autoCloseTime) {
            timer = setTimeout(onClose, autoCloseTime);
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [open, autoCloseTime, onClose]);

    const tooltipElement = (
        <Tooltip
            id={id}
            anchorSelect={anchorSelect}
            className={`custom-tooltip${className ? " " + className : ""}`}
            place={placement}
            offset={10}
            positionStrategy="fixed"
            imperativeModeOnly
            delayShow={openDelay}
            clickable
            style={mergedStyle}
            render={() => (
                <div className="content">
                    <motion.div
                        initial={animate ? "hidden" : false}
                        animate={animate && open ? "visible" : false}
                        exit="exit"
                        variants={bounceVariants}
                        transition={bounceTransition}
                    >
                        <button onClick={onClose} className="close-btn tw-cursor-pointer">
                            <IoCloseCircleOutline fontSize={17} />
                        </button>

                        <div className="content-texts">
                            {customContent ? customContent : <p className="mb-0">{contentString}</p>}
                        </div>
                    </motion.div>
                </div>
            )}
            isOpen={open}
        />
    );

    if (typeof document === "undefined") {
        return tooltipElement;
    }

    return createPortal(tooltipElement, document.body);
}

function CustomTooltip(props: CustomTooltipProps) {
    if (props.adjustForVirtualKeyboard) {
        return <PuzzleTileCustomTooltip {...props} />;
    }
    return <DefaultCustomTooltip {...props} />;
}

export default CustomTooltip;
